import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { formatApiError } from '../lib/errors';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ShoppingCart, Trash2, Plus, Minus, Store, CreditCard, ArrowLeft, Wallet, Building2, Luggage, ShieldCheck } from 'lucide-react';
import { resolveAssetUrl } from '../lib/assets';
import { getClsCart, removeFromClsCart, clearClsCart, CLS_CART_EVENT } from '../lib/classifiedsCart';
import ClassifiedPurchaseDialog from '../components/ClassifiedPurchaseDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [cart, setCart] = useState({ groups: [] });
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [payingWithWallet, setPayingWithWallet] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddrId, setSelectedAddrId] = useState('profile');
  const [newAddr, setNewAddr] = useState({ name: '', address: '', postal_code: '', region: '', phone: '' });
  const [saveNewAddr, setSaveNewAddr] = useState(true);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [asCompany, setAsCompany] = useState(false);
  const [clsItems, setClsItems] = useState(getClsCart());
  const [clsPurchaseOpen, setClsPurchaseOpen] = useState(false);
  const [billing, setBilling] = useState({ legal_name: '', registration_number: '', vat_number: '', legal_address: '' });
  const billingPayload = () => (asCompany && (billing.legal_name || billing.registration_number) ? { as_company: true, ...billing } : null);
  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);

  const profileAddress = user ? {
    name: user.name || '', address: user.address || '', postal_code: user.postal_code || '',
    region: user.region || '', phone: user.phone || '',
  } : {};
  const profileHasAddress = !!(profileAddress.address);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    const loadData = async () => {
      try {
        const [cartRes, walletRes, addrRes] = await Promise.all([
          axios.get(`${API}/cart`),
          axios.get(`${API}/wallet`).catch(() => ({ data: { balance: 0 } })),
          axios.get(`${API}/profile/addresses`).catch(() => ({ data: { addresses: [] } })),
        ]);
        setCart(cartRes.data);
        setWalletBalance(walletRes.data.balance || 0);
        const addrs = addrRes.data.addresses || [];
        setSavedAddresses(addrs);
        const defaultAddr = addrs.find((a) => a.is_default);
        if (defaultAddr) {
          setSelectedAddrId(defaultAddr.id);
        } else if (!user.address && addrs.length > 0) {
          setSelectedAddrId(addrs[0].id);
        } else if (!user.address) {
          setSelectedAddrId('new');
        }
        // Buy-as-company: available to any user with an active company (seller
        // and/or a business page). Prefill legal info when we have it.
        try {
          let eligible = !!user.is_seller;
          let ci = null;
          if (user.is_seller) {
            const sp = await axios.get(`${API}/shop-profile`).catch(() => null);
            const li = sp?.data?.seller_legal_info;
            if (li && (li.legal_name || li.registration_number)) ci = li;
          }
          const bp = await axios.get(`${API}/business-page/me`).catch(() => null);
          const bl = bp?.data;
          if (bl && bl.template) eligible = true;
          if (!ci && bl && bl.legal_same_as_shop === false && bl.legal_info && (bl.legal_info.legal_name || bl.legal_info.registration_number)) ci = bl.legal_info;
          if (eligible) {
            setCompanyInfo(ci || {});
            if (ci) setBilling({ legal_name: ci.legal_name || '', registration_number: ci.registration_number || '', vat_number: ci.vat_number || '', legal_address: ci.legal_address || '' });
          }
        } catch (e) { /* no company */ }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    loadData();
  }, [user, navigate]);

  // Resolve the chosen shipping address, saving a new one if requested.
  const resolveShippingAddress = async () => {
    if (selectedAddrId === 'profile') return profileAddress;
    if (selectedAddrId === 'new') {
      if (!newAddr.address.trim()) {
        alert(tr('Lūdzu ievadi adresi', 'Please enter an address', 'Введите адрес'));
        return undefined;
      }
      if (saveNewAddr) {
        try {
          const { data } = await axios.post(`${API}/profile/addresses`, newAddr);
          setSavedAddresses(data.addresses || []);
        } catch (e) { /* still proceed with the entered address */ }
      }
      return { ...newAddr };
    }
    const found = savedAddresses.find((a) => a.id === selectedAddrId);
    return found || profileAddress;
  };

  const fetchCart = async () => {
    try {
      const { data } = await axios.get(`${API}/cart`);
      setCart(data);
    } catch (e) { console.error(e); }
  };

  // Keep the classifieds (C2C escrow) cart in sync — it lives in localStorage
  // and is shown here as its own section so buyers find it in the main cart.
  useEffect(() => {
    const sync = () => setClsItems(getClsCart());
    window.addEventListener(CLS_CART_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => { window.removeEventListener(CLS_CART_EVENT, sync); window.removeEventListener('storage', sync); };
  }, []);

  const clsTotal = clsItems.reduce((s, i) => s + (Number(i.price) || 0), 0);

  const removeItem = async (itemId) => {
    try {
      await axios.delete(`${API}/cart/${itemId}`);
      fetchCart();
    } catch (e) { console.error(e); }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart`);
      fetchCart();
    } catch (e) { console.error(e); }
  };

  const handleCheckout = async () => {
    const shipping_address = await resolveShippingAddress();
    if (shipping_address === undefined) return;
    setCheckingOut(true);
    try {
      const { data } = await axios.post(`${API}/cart/checkout`, {
        origin_url: window.location.origin,
        shipping_address,
        billing: billingPayload(),
      });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      console.error(e);
      alert(formatApiError(e, 'Checkout error'));
    } finally { setCheckingOut(false); }
  };

  const handleWalletPay = async () => {
    const shipping_address = await resolveShippingAddress();
    if (shipping_address === undefined) return;
    setPayingWithWallet(true);
    try {
      const { data } = await axios.post(`${API}/wallet/pay`, { type: 'cart', shipping_address, billing: billingPayload() });
      // Wallet purchases create the order synchronously, so jump straight to success.
      if (data.order_id) {
        navigate(`/order/success?order_id=${data.order_id}`);
      } else {
        navigate('/orders');
      }
      fetchCart();
    } catch (e) {
      alert(formatApiError(e, 'Kļūda'));
    } finally { setPayingWithWallet(false); }
  };

  const totalItems = cart.groups.reduce((sum, g) => sum + g.items.length, 0);
  const totalPrice = cart.groups.reduce((sum, g) =>
    sum + g.items.reduce((s, i) => s + (i.price || 0) * (i.quantity || 1), 0), 0);

  if (loading) return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D97736]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="cart-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-8">
          <button onClick={() => navigate(-1)} className="text-[#5C5C5C] hover:text-[#121212]">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-outfit text-3xl font-bold text-[#121212] flex items-center gap-3">
            <ShoppingCart className="w-8 h-8 text-[#D97736]" />
            {language === 'lv' ? 'Grozs' : language === 'ru' ? 'Корзина' : 'Cart'}
            {totalItems > 0 && <Badge className="bg-[#D97736] text-white">{totalItems}</Badge>}
          </h1>
        </div>

        {clsItems.length > 0 && (
          <div className="mb-6 bg-white border border-[#2F7E79]/30 rounded-2xl overflow-hidden" data-testid="cart-classifieds-section">
            <div className="px-5 py-3 bg-[#E6F2F0] flex items-center gap-2 flex-wrap">
              <Luggage className="w-4 h-4 text-[#2F7E79]" />
              <span className="font-semibold text-[#121212]">{tr('Privātie sludinājumi', 'Private classifieds', 'Частные объявления')}</span>
              <span className="inline-flex items-center gap-1 text-xs text-[#2F7E79]"><ShieldCheck className="w-3.5 h-3.5" /> {tr('drošs darījums', 'secure escrow', 'безопасная сделка')}</span>
            </div>
            <div className="divide-y divide-[#E5E4E0]">
              {clsItems.map((it) => (
                <div key={it.id} className="flex items-center gap-4 p-4" data-testid={`cls-cart-item-${it.id}`}>
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#F3F2EE] flex-shrink-0 flex items-center justify-center">
                    {it.image ? <img src={resolveAssetUrl(it.image)} alt="" className="w-full h-full object-cover" /> : <Luggage className="w-6 h-6 text-[#C7C5BF]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/sludinajums/${it.slug || it.id}`} className="font-medium text-[#121212] hover:text-[#2F7E79] text-sm truncate block">{it.title}</Link>
                    {it.sellerName && <div className="text-xs text-[#5C5C5C] truncate">{it.sellerName}</div>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-semibold text-[#121212]">{it.price != null ? `€${Number(it.price).toFixed(2)}` : '—'}</div>
                    <button onClick={() => removeFromClsCart(it.id)} className="text-red-400 hover:text-red-600 mt-1" data-testid={`cls-cart-remove-${it.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 flex items-center justify-between gap-3 bg-[#FAFAF8] flex-wrap">
              <div className="text-sm text-[#5C5C5C]">{tr('Kopā', 'Total', 'Итого')}: <span className="font-bold text-[#2F7E79]" data-testid="cls-cart-total">€{clsTotal.toFixed(2)}</span></div>
              <Button onClick={() => setClsPurchaseOpen(true)} className="rounded-full bg-[#2F7E79] hover:bg-[#276b66] text-white gap-2" data-testid="cls-cart-checkout-btn">
                <ShieldCheck className="w-4 h-4" /> {tr('Pirkt caur KoLV', 'Buy via KoLV', 'Купить через KoLV')}
              </Button>
            </div>
          </div>
        )}

        {cart.groups.length === 0 ? (
          clsItems.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-[#E5E4E0]">
            <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-[#5C5C5C] opacity-20" />
            <h3 className="font-outfit text-xl font-semibold text-[#121212] mb-2">
              {language === 'lv' ? 'Grozs ir tukšs' : language === 'ru' ? 'Корзина пуста' : 'Cart is empty'}
            </h3>
            <Link to="/listings">
              <Button className="mt-4 bg-[#D97736] hover:bg-[#C06528] text-white">
                {language === 'lv' ? 'Pārlūkot produktus' : language === 'ru' ? 'Смотреть товары' : 'Browse Products'}
              </Button>
            </Link>
          </div>
          ) : null
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Items grouped by seller */}
            <div className="lg:col-span-2 space-y-4">
              {cart.groups.map(group => (
                <div key={group.seller_id} className="bg-white border border-[#E5E4E0] rounded-2xl overflow-hidden">
                  <div className="px-5 py-3 bg-[#F3F2EE] flex items-center gap-2">
                    <Store className="w-4 h-4 text-[#4A6B53]" />
                    <Link to={`/shop/${group.seller_id}`} className="font-medium text-[#121212] hover:text-[#D97736]">
                      {group.seller_name}
                    </Link>
                  </div>
                  <div className="divide-y divide-[#E5E4E0]">
                    {group.items.map(item => (
                      <div key={item.id} className="flex items-center gap-4 p-4" data-testid={`cart-item-${item.id}`}>
                        <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#F3F2EE] flex-shrink-0">
                          {item.image && <img src={resolveAssetUrl(item.image)} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <Link to={`/listing/${item.listing_id}`} className="font-medium text-[#121212] hover:text-[#D97736] text-sm truncate block">
                            {item.title}
                          </Link>
                          <div className="text-sm text-[#5C5C5C]">
                            {item.quantity} x {item.price ? `€${item.price}` : (language === 'lv' ? 'Cena nav norādīta' : language === 'ru' ? 'Цена не указана' : 'Price not set')}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="font-semibold text-[#121212]">
                            {item.price ? `€${(item.price * item.quantity).toFixed(2)}` : '—'}
                          </div>
                          <button onClick={() => removeItem(item.id)} className="text-red-400 hover:text-red-600 mt-1">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6 h-fit sticky top-24">
              <h3 className="font-outfit font-semibold text-lg text-[#121212] mb-4">
                {language === 'lv' ? 'Pasūtījuma kopsavilkums' : language === 'ru' ? 'Итог заказа' : 'Order Summary'}
              </h3>
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-[#5C5C5C]">{language === 'lv' ? 'Produkti' : language === 'ru' ? 'Товары' : 'Items'} ({totalItems})</span>
                  <span className="text-[#121212]">€{totalPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <div className="flex items-center gap-1 text-[#5C5C5C]">
                    <span>{language === 'lv' ? 'Platformas servisa maksa' : language === 'ru' ? 'Сервисный сбор платформы' : 'Platform service fee'}</span>
                    <Link to="/maksas-un-komisijas" className="text-[#D97736] hover:underline text-xs" title={language === 'lv' ? 'Kāpēc?' : language === 'ru' ? 'Почему?' : 'Why?'}>
                      ⓘ
                    </Link>
                  </div>
                  <span className="text-[#5C5C5C]">+ €0.40</span>
                </div>
                <div className="text-xs text-[#5C5C5C] leading-snug -mt-1">
                  {language === 'lv'
                    ? 'Piemērota tikai maksājot ar karti. Maciņa maksājumos — bez papildu maksām.'
                    : language === 'ru'
                    ? 'Применяется только при оплате картой. Оплата кошельком — без доп. сборов.'
                    : 'Only when paying by card. Wallet payments — no extra fees.'}
                </div>
                <div className="border-t border-[#E5E4E0] pt-3 space-y-1">
                  <div className="flex justify-between text-xs text-[#5C5C5C]">
                    <span>{language === 'lv' ? 'Ar karti' : language === 'ru' ? 'Картой' : 'By card'}</span>
                    <span className="font-mono">€{(totalPrice + 0.40).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-[#5C5C5C]">
                    <span>{language === 'lv' ? 'Ar maciņu' : language === 'ru' ? 'Кошельком' : 'With wallet'}</span>
                    <span className="font-mono">€{totalPrice.toFixed(2)}</span>
                  </div>
                </div>
                <div className="border-t border-[#E5E4E0] pt-3 flex justify-between">
                  <span className="font-semibold text-[#121212]">{language === 'lv' ? 'Kopā maksā' : language === 'ru' ? 'К оплате' : 'You pay'}</span>
                  <span className="font-bold text-xl text-[#D97736]" data-testid="cart-total">
                    €{totalPrice.toFixed(2)}<span className="text-sm text-[#5C5C5C]"> – €{(totalPrice + 0.40).toFixed(2)}</span>
                  </span>
                </div>
              </div>

              {/* Delivery address */}
              <div className="mb-5 border-t border-[#E5E4E0] pt-4" data-testid="delivery-address">
                <h4 className="font-medium text-[#121212] text-sm mb-3">{tr('Piegādes adrese', 'Delivery address', 'Адрес доставки')}</h4>
                <div className="space-y-2">
                  {profileHasAddress && (
                    <label className={`flex items-start gap-2 p-3 rounded-xl border cursor-pointer text-sm ${selectedAddrId === 'profile' ? 'border-[#D97736] bg-[#FFF6F0]' : 'border-[#E5E4E0]'}`} data-testid="addr-option-profile">
                      <input type="radio" checked={selectedAddrId === 'profile'} onChange={() => setSelectedAddrId('profile')} className="mt-1 accent-[#D97736]" />
                      <span className="min-w-0">
                        <span className="font-medium text-[#121212]">{tr('Mana profila adrese', 'My profile address', 'Мой адрес')}</span>
                        <span className="block text-[#5C5C5C] truncate">{profileAddress.address}{profileAddress.postal_code ? `, ${profileAddress.postal_code}` : ''}</span>
                      </span>
                    </label>
                  )}
                  {savedAddresses.map((a) => (
                    <label key={a.id} className={`flex items-start gap-2 p-3 rounded-xl border cursor-pointer text-sm ${selectedAddrId === a.id ? 'border-[#D97736] bg-[#FFF6F0]' : 'border-[#E5E4E0]'}`} data-testid={`addr-option-${a.id}`}>
                      <input type="radio" checked={selectedAddrId === a.id} onChange={() => setSelectedAddrId(a.id)} className="mt-1 accent-[#D97736]" />
                      <span className="min-w-0 flex-1">
                        <span className="font-medium text-[#121212]">{a.label || a.name || tr('Saglabāta adrese', 'Saved address', 'Сохранённый')}</span>
                        <span className="block text-[#5C5C5C] truncate">{a.address}{a.postal_code ? `, ${a.postal_code}` : ''}</span>
                      </span>
                    </label>
                  ))}
                  <label className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer text-sm ${selectedAddrId === 'new' ? 'border-[#D97736] bg-[#FFF6F0]' : 'border-[#E5E4E0]'}`} data-testid="addr-option-new">
                    <input type="radio" checked={selectedAddrId === 'new'} onChange={() => setSelectedAddrId('new')} className="accent-[#D97736]" />
                    <span className="font-medium text-[#121212]">{tr('Cita adrese', 'Other address', 'Другой адрес')}</span>
                  </label>
                  {selectedAddrId === 'new' && (
                    <div className="space-y-2 p-3 rounded-xl bg-[#FAFAFA] border border-[#E5E4E0]" data-testid="new-address-form">
                      <input value={newAddr.name} onChange={(e) => setNewAddr({ ...newAddr, name: e.target.value })} placeholder={tr('Saņēmēja vārds', 'Recipient name', 'Имя')} className="w-full h-10 px-3 rounded-lg border border-[#E5E4E0] text-sm" data-testid="new-addr-name" />
                      <input value={newAddr.address} onChange={(e) => setNewAddr({ ...newAddr, address: e.target.value })} placeholder={tr('Adrese *', 'Address *', 'Адрес *')} className="w-full h-10 px-3 rounded-lg border border-[#E5E4E0] text-sm" data-testid="new-addr-address" />
                      <div className="flex gap-2">
                        <input value={newAddr.postal_code} onChange={(e) => setNewAddr({ ...newAddr, postal_code: e.target.value })} placeholder="LV-1001" className="w-1/2 h-10 px-3 rounded-lg border border-[#E5E4E0] text-sm" data-testid="new-addr-postal" />
                        <input value={newAddr.phone} onChange={(e) => setNewAddr({ ...newAddr, phone: e.target.value })} placeholder={tr('Tālrunis', 'Phone', 'Телефон')} className="w-1/2 h-10 px-3 rounded-lg border border-[#E5E4E0] text-sm" data-testid="new-addr-phone" />
                      </div>
                      <label className="flex items-center gap-2 text-xs text-[#5C5C5C] cursor-pointer">
                        <input type="checkbox" checked={saveNewAddr} onChange={(e) => setSaveNewAddr(e.target.checked)} className="accent-[#D97736]" data-testid="save-new-addr" />
                        {tr('Saglabāt šo adresi turpmākiem pasūtījumiem', 'Save this address for future orders', 'Сохранить адрес')}
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Buy as company — only when the user has an active company */}
              {companyInfo && (
                <div className="mb-5 border-t border-[#E5E4E0] pt-4" data-testid="buy-as-company">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={asCompany} onChange={(e) => setAsCompany(e.target.checked)} className="accent-[#D97736] w-4 h-4" data-testid="buy-as-company-toggle" />
                    <span className="text-sm font-medium text-[#121212] flex items-center gap-1.5"><Building2 className="w-4 h-4 text-[#4A6B53]" /> {tr('Pirkt kā uzņēmums', 'Buy as a company', 'Купить как компания')}</span>
                  </label>
                  {asCompany && (
                    <div className="mt-3 grid grid-cols-1 gap-2" data-testid="company-billing-fields">
                      <input value={billing.legal_name} onChange={(e) => setBilling({ ...billing, legal_name: e.target.value })} placeholder={tr('Uzņēmuma nosaukums', 'Company name', 'Название компании')} className="text-sm border border-[#E5E4E0] rounded-lg px-3 py-2" data-testid="billing-legal-name" />
                      <div className="grid grid-cols-2 gap-2">
                        <input value={billing.registration_number} onChange={(e) => setBilling({ ...billing, registration_number: e.target.value })} placeholder={tr('Reģ. Nr.', 'Reg. No.', 'Рег. №')} className="text-sm border border-[#E5E4E0] rounded-lg px-3 py-2" data-testid="billing-regnr" />
                        <input value={billing.vat_number} onChange={(e) => setBilling({ ...billing, vat_number: e.target.value })} placeholder={tr('PVN Nr.', 'VAT No.', 'НДС №')} className="text-sm border border-[#E5E4E0] rounded-lg px-3 py-2" data-testid="billing-vat" />
                      </div>
                      <input value={billing.legal_address} onChange={(e) => setBilling({ ...billing, legal_address: e.target.value })} placeholder={tr('Juridiskā adrese', 'Legal address', 'Юридический адрес')} className="text-sm border border-[#E5E4E0] rounded-lg px-3 py-2" data-testid="billing-address" />
                      <p className="text-xs text-[#5C5C5C]">{tr('Šie dati parādīsies rēķinā.', 'These details will appear on the invoice.', 'Эти данные появятся в счёте.')}</p>
                    </div>
                  )}
                </div>
              )}


              <Button
                onClick={handleCheckout}
                disabled={checkingOut || totalPrice <= 0}
                className="w-full h-12 bg-[#D97736] hover:bg-[#C06528] text-white font-medium text-base"
                data-testid="checkout-btn"
              >
                {checkingOut ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {language === 'lv' ? 'Apstrādā...' : language === 'ru' ? 'Обработка...' : 'Processing...'}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    {language === 'lv' ? 'Apmaksāt ar karti' : language === 'ru' ? 'Оплатить картой' : 'Pay with Card'}
                  </span>
                )}
              </Button>

              {/* Wallet payment option */}
              <div className="mt-3 p-3 bg-[#F3F2EE] rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Wallet className="w-4 h-4 text-[#4A6B53]" />
                    <span className="text-[#5C5C5C]">{language === 'lv' ? 'Maciņš' : language === 'ru' ? 'Кошелёк' : 'Wallet'}</span>
                  </div>
                  <span className={`text-sm font-semibold ${walletBalance >= totalPrice ? 'text-[#4A6B53]' : 'text-red-500'}`}>
                    €{walletBalance.toFixed(2)}
                  </span>
                </div>
                <div className="text-xs text-[#5C5C5C] mb-2" data-testid="cart-wallet-projection">
                  {walletBalance >= totalPrice
                    ? (language === 'lv'
                        ? `Pēc pirkuma paliks €${(walletBalance - totalPrice).toFixed(2)}`
                        : language === 'ru'
                        ? `После покупки останется €${(walletBalance - totalPrice).toFixed(2)}`
                        : `€${(walletBalance - totalPrice).toFixed(2)} left after purchase`)
                    : (
                      <>
                        {language === 'lv'
                          ? `Trūkst €${(totalPrice - walletBalance).toFixed(2)} · `
                          : language === 'ru'
                          ? `Не хватает €${(totalPrice - walletBalance).toFixed(2)} · `
                          : `€${(totalPrice - walletBalance).toFixed(2)} short · `}
                        <Link to="/wallet" className="text-[#D97736] hover:underline font-medium" data-testid="cart-wallet-topup">
                          {language === 'lv' ? 'Papildināt maciņu' : language === 'ru' ? 'Пополнить кошелёк' : 'Top up wallet'}
                        </Link>
                      </>
                    )}
                </div>
                <Button
                  onClick={handleWalletPay}
                  disabled={payingWithWallet || walletBalance < totalPrice || totalPrice <= 0}
                  variant="outline"
                  className="w-full h-10 border-[#4A6B53] text-[#4A6B53] hover:bg-[#4A6B53] hover:text-white"
                  data-testid="wallet-pay-btn"
                >
                  {payingWithWallet ? (
                    <span>{language === 'lv' ? 'Apstrādā...' : language === 'ru' ? 'Обработка...' : 'Processing...'}</span>
                  ) : walletBalance >= totalPrice ? (
                    <span className="flex items-center gap-2">
                      <Wallet className="w-4 h-4" />
                      {language === 'lv' ? 'Maksāt ar maciņu' : language === 'ru' ? 'Оплатить кошельком' : 'Pay with Wallet'}
                    </span>
                  ) : (
                    <span className="text-xs">
                      {language === 'lv' ? 'Nepietiekams atlikums' : language === 'ru' ? 'Недостаточно средств' : 'Insufficient balance'}
                    </span>
                  )}
                </Button>
              </div>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" className="w-full mt-2 text-[#5C5C5C] hover:text-red-500">
                    {language === 'lv' ? 'Iztukšot grozu' : language === 'ru' ? 'Очистить корзину' : 'Clear cart'}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{language === 'lv' ? 'Iztukšot grozu?' : language === 'ru' ? 'Очистить корзину?' : 'Clear cart?'}</AlertDialogTitle>
                    <AlertDialogDescription>{language === 'lv' ? 'Visi produkti tiks noņemti' : language === 'ru' ? 'Все товары будут удалены' : 'All items will be removed'}</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{language === 'lv' ? 'Atcelt' : language === 'ru' ? 'Отмена' : 'Cancel'}</AlertDialogCancel>
                    <AlertDialogAction className="bg-red-500 hover:bg-red-600" onClick={clearCart}>
                      {language === 'lv' ? 'Iztukšot' : language === 'ru' ? 'Очистить' : 'Clear'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        )}
        <ClassifiedPurchaseDialog
          open={clsPurchaseOpen}
          onOpenChange={setClsPurchaseOpen}
          ids={clsItems.map((i) => i.id)}
          onPaid={() => { clearClsCart(); setClsItems([]); navigate('/sludinajumi/darijumi'); }}
        />
      </div>
    </div>
  );
}
